import { computed, getCurrentInstance, onUnmounted, ref, unref, watch } from 'vue'
import { useEventListener, useIntervalFn } from '@vueuse/core'

const SECOND_MS = 1000

const resolveInput = (value) => {
  if (typeof value === 'function') {
    return value()
  }
  return unref(value)
}

const normalizeTimestamp = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return null
  }

  return numeric < 1e12 ? Math.floor(numeric * SECOND_MS) : Math.floor(numeric)
}

const formatParts = (ms) => {
  const safeMs = Math.max(0, Number(ms) || 0)
  const totalSeconds = Math.floor(safeMs / SECOND_MS)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds,
    totalMilliseconds: safeMs,
  }
}

const getPerfNow = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

const useServerCountdown = (options = {}) => {
  const {
    targetTime,
    getServerTime,
    tickInterval = SECOND_MS,
    resyncInterval = 60 * SECOND_MS,
    immediate = true,
    autoStart = true,
    syncWhenVisible = true,
    onError,
  } = options

  const ready = ref(false)
  const running = ref(false)
  const syncing = ref(false)
  const serverNowAtSync = ref(0)
  const perfNowAtSync = ref(0)
  const estimatedServerNow = ref(Date.now())
  const syncAt = ref(0)
  const targetAt = ref(normalizeTimestamp(resolveInput(targetTime)))

  if (targetTime !== undefined) {
    watch(
      () => normalizeTimestamp(resolveInput(targetTime)),
      (nextTarget) => {
        targetAt.value = nextTarget
      }
    )
  }

  let stopVisibilityListener = null

  const updateEstimatedServerNow = () => {
    if (!ready.value) {
      estimatedServerNow.value = Date.now()
      return estimatedServerNow.value
    }

    const elapsed = getPerfNow() - perfNowAtSync.value
    estimatedServerNow.value = Math.floor(serverNowAtSync.value + elapsed)
    return estimatedServerNow.value
  }

  const readServerTime = async () => {
    if (typeof getServerTime !== 'function') {
      throw new Error('useServerCountdown 需要传入 getServerTime 函数')
    }

    const serverValue = await getServerTime()
    const parsed = normalizeTimestamp(serverValue)
    if (parsed === null) {
      throw new Error('getServerTime 必须返回有效时间戳')
    }
    return parsed
  }

  const sync = async () => {
    if (syncing.value) {
      return ready.value
    }

    syncing.value = true
    try {
      const serverNow = await readServerTime()
      serverNowAtSync.value = serverNow
      perfNowAtSync.value = getPerfNow()
      syncAt.value = Date.now()
      ready.value = true
      updateEstimatedServerNow()
      return true
    } catch (error) {
      ready.value = false
      if (typeof onError === 'function') {
        onError(error)
      }
      return false
    } finally {
      syncing.value = false
    }
  }

  const tick = () => {
    updateEstimatedServerNow()
  }

  const safeTickInterval = Math.max(250, Number(tickInterval) || SECOND_MS)
  const safeResyncInterval = Math.max(0, Number(resyncInterval) || 0)

  const {
    pause: pauseTick,
    resume: resumeTick,
  } = useIntervalFn(tick, safeTickInterval, { immediate: false, immediateCallback: false })

  const {
    pause: pauseResync,
    resume: resumeResync,
  } = useIntervalFn(sync, safeResyncInterval || SECOND_MS, { immediate: false, immediateCallback: false })

  const onVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      await sync()
    }
  }

  const start = async () => {
    if (running.value) {
      return true
    }

    if (immediate || !ready.value) {
      const ok = await sync()
      if (!ok || !ready.value) {
        running.value = false
        return false
      }
    }

    tick()
    resumeTick()

    if (safeResyncInterval > 0) {
      resumeResync()
    }

    if (syncWhenVisible && typeof document !== 'undefined' && !stopVisibilityListener) {
      stopVisibilityListener = useEventListener(document, 'visibilitychange', onVisibilityChange)
    }

    running.value = true
    return true
  }

  const stop = () => {
    pauseTick()
    pauseResync()

    if (stopVisibilityListener) {
      stopVisibilityListener()
      stopVisibilityListener = null
    }

    running.value = false
  }

  const restart = async () => {
    stop()
    return start()
  }

  const setTargetTime = (value) => {
    targetAt.value = normalizeTimestamp(resolveInput(value))
  }

  const remainingMs = computed(() => {
    if (!targetAt.value) {
      return 0
    }
    return Math.max(0, targetAt.value - estimatedServerNow.value)
  })

  const expired = computed(() => remainingMs.value <= 0)
  const parts = computed(() => formatParts(remainingMs.value))

  const vm = getCurrentInstance()
  if (vm) {
    onUnmounted(() => {
      stop()
    })
  }

  if (autoStart) {
    start()
  }

  return {
    ready,
    running,
    syncing,
    syncAt,
    serverNowAtSync,
    estimatedServerNow,
    targetAt,
    remainingMs,
    expired,
    parts,
    sync,
    start,
    stop,
    restart,
    tick,
    setTargetTime,
    formatParts,
  }
}

export default useServerCountdown
